/**
 * Shared display vocabulary for the Social Media Strategy document: the pillar
 * color system, enum -> {label, color, icon} maps, and small formatting helpers.
 *
 * This is the label and tone layer for the enums in `strategyDocument.type.ts`.
 * It is keyed on the values the agent actually writes, so a surface rendering the
 * document should resolve its labels and colors from here rather than inventing
 * its own — that is what keeps a pillar, a format or a stance reading the same
 * on the strategy page, the calendar and a concept.
 *
 * Several maps here have no caller right now: the strategy read-surface was
 * deleted and is being rebuilt (see `StrategyDocumentView.tsx`). They are kept
 * deliberately, because the vocabulary is correct and re-deriving it from the
 * backend enums by hand is how wrong labels get shipped.
 */
import {
  Activity,
  type LucideIcon,
  MessagesSquare,
  Radar,
  Swords,
  TrendingUp,
  Users,
} from "lucide-react";

// ── Color helper ─────────────────────────────────────────────────────────────

/**
 * `#rrggbb` + alpha -> `rgba(...)`. Lets us tint a single brand hue at any opacity.
 *
 * Anything that is not a hex literal (notably the `var(--co-*)` tokens the tone maps
 * use) cannot be parsed here, so it is handed to the browser as `color-mix` instead —
 * parsing it as hex would silently yield `rgba(NaN, NaN, NaN, a)`.
 */
export function withAlpha(color: string, alpha: number): string {
  if (!color.startsWith("#")) {
    return `color-mix(in srgb, ${color} ${Math.round(alpha * 100)}%, transparent)`;
  }
  const h = color.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ── Pillar palette ───────────────────────────────────────────────────────────
// A pillar is content identity, not a chart series. The strategy, calendar,
// concept and publishing surfaces resolve it from the same two approved
// primitives so the meaning survives across the whole workflow.
const PILLAR_PURPLE = "#6a00ba";
const PILLAR_BLUE = "#207cf5";
export const PILLAR_IDENTITY_CAPACITY = 64;

function channel(hex: string, offset: number): number {
  return Number.parseInt(hex.slice(offset, offset + 2), 16);
}

function mixHex(from: string, to: string, fromShare: number): string {
  const mixed = [1, 3, 5].map((offset) =>
    Math.round(channel(from, offset) * fromShare + channel(to, offset) * (1 - fromShare)),
  );
  return `#${mixed.map((value) => value.toString(16).padStart(2, "0")).join("")}`;
}

/**
 * Farthest-first positions on the Purple -> Blue line: endpoints first, then
 * midpoint, then the midpoint of each remaining gap. The first five therefore
 * resolve P, B, 50/50, 75/25 and 25/75—visually separated as pillars are added.
 */
function pillarPurpleShare(index: number): number {
  const parsed = Math.trunc(index);
  const safeIndex = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
  if (safeIndex === 0) return 1;
  if (safeIndex === 1) return 0;

  let remaining = safeIndex - 2;
  let denominator = 2;
  let levelSize = 1;
  while (remaining >= levelSize) {
    remaining -= levelSize;
    denominator *= 2;
    levelSize *= 2;
  }
  return (denominator - 1 - remaining * 2) / denominator;
}

/**
 * Canonical content-pillar color. Runtime interpolation keeps vector PDF output
 * and browser UI byte-for-byte aligned without storing extra chromatic swatches.
 */
export function pillarColor(index: number): string {
  return mixHex(PILLAR_PURPLE, PILLAR_BLUE, pillarPurpleShare(index));
}

/**
 * Browser-only display tone for a pillar identity. Light surfaces keep the exact
 * Purple → Blue hue; dark surfaces lift that same hue toward the Text token so
 * small marks and chart fills remain distinguishable from Surface. PDF export
 * intentionally continues to use `pillarColor()` because its ground is white.
 */
export function pillarUiTone(hue: string): string {
  return `color-mix(in srgb, ${hue} var(--co-pillar-hue-weight), var(--co-text))`;
}

/** Theme-aware browser tone for non-pillar charts and categorical marks. */
export function uiColorTone(hue: string): string {
  return `color-mix(in srgb, ${hue} var(--co-ui-hue-weight), var(--co-text))`;
}

export interface OrderedPillarIdentity {
  id: string;
  order?: number;
}

/**
 * One resolver for operational product surfaces that inherit the active strategy.
 * Explicit promoted order wins; source order is the deterministic fallback for
 * older fixtures. Snapshot-only ids are appended so an older calendar post can
 * still carry an identity mark when it is no longer in the active strategy.
 * The brand-derived scale keeps every displayed pillar distinct and stable
 * without introducing unapproved hue families or using status-only Green/Lime.
 */
export function buildPillarHueMap(
  pillars: readonly OrderedPillarIdentity[],
  fallbackIds: readonly (string | null | undefined)[] = [],
): Map<string, string> {
  const ordered = pillars
    .map((pillar, sourceIndex) => ({ pillar, sourceIndex }))
    .sort((a, b) => {
      const aOrder = Number.isFinite(a.pillar.order) ? a.pillar.order! : a.sourceIndex;
      const bOrder = Number.isFinite(b.pillar.order) ? b.pillar.order! : b.sourceIndex;
      return aOrder - bOrder || a.sourceIndex - b.sourceIndex;
    });
  const ids: string[] = [];
  const seen = new Set<string>();

  for (const { pillar } of ordered) {
    if (seen.has(pillar.id)) continue;
    seen.add(pillar.id);
    ids.push(pillar.id);
  }
  const stableFallbackIds = [...new Set(fallbackIds.filter((id): id is string => Boolean(id)))].sort(
    (a, b) => a.localeCompare(b),
  );
  for (const id of stableFallbackIds) {
    if (seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }

  if (ids.length > PILLAR_IDENTITY_CAPACITY) {
    throw new RangeError(
      `A brand can display at most ${PILLAR_IDENTITY_CAPACITY} distinct pillar identities.`,
    );
  }

  return new Map(ids.map((id, index) => [id, pillarColor(index)]));
}

/** Generic ordered series palette (posture mix, ad-hoc proportional bars). */
export const SERIES_PALETTE = [
  "#207cf5",
  "#6a00ba",
  "#a78bfa",
  "#ff9a5c",
  "#34d3c0",
  "#f472b6",
  "#fbbf24",
  "#94a3b8",
];

export function seriesColor(index: number): string {
  return SERIES_PALETTE[index % SERIES_PALETTE.length];
}

// ── Research streams ─────────────────────────────────────────────────────────

export const RESEARCH_STREAM_META: Record<
  string,
  { label: string; Icon: LucideIcon; hex: string }
> = {
  category: { label: "Category", Icon: Radar, hex: "#207cf5" },
  audience: { label: "Audience", Icon: Users, hex: "#34d3c0" },
  competitors: { label: "Competitors", Icon: Swords, hex: "#f472b6" },
  brand_audit: { label: "Brand audit", Icon: Activity, hex: "#6a00ba" },
  trends: { label: "Trends", Icon: TrendingUp, hex: "#ff9a5c" },
  ugc_social_listening: { label: "Social listening", Icon: MessagesSquare, hex: "#a78bfa" },
};

/** Canonical order for the six research streams. */
export const RESEARCH_STREAM_ORDER = [
  "category",
  "audience",
  "competitors",
  "brand_audit",
  "trends",
  "ugc_social_listening",
] as const;

// ── Objective types ──────────────────────────────────────────────────────────

/**
 * Goal type is free text server-side (a closed list of social outcomes could not
 * express "more sales"), so this is a display-polish map, NOT a set of allowed
 * values — anything missing falls through to humanize(). Only add a key here when
 * title-casing the raw string would read wrong.
 */
export const OBJECTIVE_TYPE_LABELS: Record<string, string> = {
  reach: "Reach",
  engagement: "Engagement",
  shares: "Shares",
  follows: "Follows",
  brand_recall: "Brand recall",
  sales: "Sales",
  leads: "Leads",
  website_traffic: "Website traffic",
  signups: "Sign-ups",
  store_visits: "Store visits",
  app_installs: "App installs",
  community: "Community",
  retention: "Retention",
  thought_leadership: "Thought leadership",
  talent_attraction: "Talent attraction",
};

// ── Formats ──────────────────────────────────────────────────────────────────

export const FORMAT_LABELS: Record<string, string> = {
  reel: "Reels",
  static: "Static",
  carousel: "Carousel",
  story: "Stories",
  shotlist: "Shotlist",
  ugc: "UGC",
  // The non-feed channels (LinkedIn, Reddit, Substack, Pinterest) — without
  // these their format mix collapses into "Other".
  text: "Text post",
  newsletter: "Newsletter",
  pin: "Pin",
  other: "Other",
};

/** Stable hue per format so a format reads the same across every mix bar. */
export const FORMAT_COLORS: Record<string, string> = {
  reel: "#207cf5",
  static: "#a78bfa",
  carousel: "#34d3c0",
  story: "#ff9a5c",
  shotlist: "#f472b6",
  ugc: "#6a00ba",
  text: "#7dd3fc",
  newsletter: "#e8a44c",
  pin: "#d9576b",
  other: "#94a3b8",
};

export function formatLabel(value: string): string {
  return FORMAT_LABELS[value] ?? humanize(value);
}
export function formatColor(value: string): string {
  return FORMAT_COLORS[value] ?? "#94a3b8";
}

// ── Status / scope ───────────────────────────────────────────────────────────

export interface ToneMeta {
  label: string;
  /** CSS color string (token or hex) for the solid mark. */
  color: string;
}

export const STATUS_META: Record<string, ToneMeta> = {
  ideas: { label: "Draft", color: "var(--co-info)" },
  active: { label: "Active", color: "var(--co-accent)" },
  archived: { label: "Archived", color: "var(--co-faint)" },
};

export const SCOPE_OPTIONS = [
  { value: "annual", label: "Annual" },
  { value: "semi", label: "Semi" },
  { value: "quarter", label: "Quarter" },
];

export const SCOPE_LABELS: Record<string, string> = {
  annual: "Annual",
  semi: "Semi",
  quarter: "Quarter",
  quarterly_refresh: "Quarter",
  campaign_deep_dive: "Quarter",
};

export function canonicalPeriodScope(scope: string): string {
  if (scope === "quarterly_refresh" || scope === "campaign_deep_dive") return "quarter";
  if (scope === "annual" || scope === "semi" || scope === "quarter") return scope;
  return "annual";
}

export function expectedRoadmapQuarters(scope: string): number | null {
  switch (canonicalPeriodScope(scope)) {
    case "annual":
      return 4;
    case "semi":
      return 2;
    case "quarter":
      return 1;
    default:
      return null;
  }
}

// ── Small qualitative enums (badges) ─────────────────────────────────────────

export const HEALTH_META: Record<string, ToneMeta> = {
  strong: { label: "Strong", color: "var(--co-live)" },
  stable: { label: "Stable", color: "var(--co-info)" },
  declining: { label: "Declining", color: "var(--co-attention)" },
  dormant: { label: "Dormant", color: "var(--co-alert)" },
};

export const MATURITY_META: Record<string, ToneMeta> = {
  emerging: { label: "Emerging", color: "#34d3c0" },
  growing: { label: "Growing", color: "var(--co-live)" },
  mature: { label: "Mature", color: "var(--co-info)" },
  saturated: { label: "Saturated", color: "var(--co-attention)" },
};

export const SENTIMENT_META: Record<string, ToneMeta> = {
  positive: { label: "Positive", color: "var(--co-live)" },
  mixed: { label: "Mixed", color: "var(--co-attention)" },
  negative: { label: "Negative", color: "var(--co-alert)" },
};

export const SHIFT_META: Record<string, ToneMeta> = {
  rising: { label: "Rising", color: "var(--co-live)" },
  declining: { label: "Declining", color: "var(--co-alert)" },
  emerging: { label: "Emerging", color: "#34d3c0" },
};

export const VOLUME_META: Record<string, ToneMeta> = {
  high: { label: "High volume", color: "var(--co-live)" },
  medium: { label: "Medium volume", color: "var(--co-attention)" },
  low: { label: "Low volume", color: "var(--co-faint)" },
};

export const EXPIRY_LABELS: Record<string, string> = {
  hours: "Expires in hours",
  days: "Days",
  weeks: "Weeks",
  evergreen: "Evergreen",
};

export const TENTPOLE_LABELS: Record<string, string> = {
  cultural: "Cultural",
  commercial: "Commercial",
  brand: "Brand",
  campaign: "Campaign",
  product_launch: "Product launch",
};

export const MOMENT_TYPE_LABELS: Record<string, string> = {
  religious: "Religious",
  national: "National",
  cultural: "Cultural",
  commercial: "Commercial",
  industry: "Industry",
  audience_life: "Audience life",
  brand_owned: "Brand owned",
  platform: "Platform",
};

/**
 * Cultural-moment stance. Follows the cockpit tone split: lime is where the brand
 * leads, muted is a deliberate pass. `skip` reads quiet on purpose — it is a
 * decision the reviewer should see, not a gap to fill.
 */
export const STANCE_META: Record<string, ToneMeta> = {
  own: { label: "Own it", color: "var(--co-accent)" },
  participate: { label: "Participate", color: "var(--co-live)" },
  observe: { label: "Observe", color: "var(--co-faint)" },
  skip: { label: "Sitting out", color: "var(--co-muted)" },
};

export const DIFFERENTIATION_LABELS: Record<string, string> = {
  crowded: "Crowded",
  contested: "Contested",
  ownable: "Ownable",
};

/** Date trust. Anything but `confirmed` must be visible on the date itself. */
export const DATE_CONFIDENCE_META: Record<string, ToneMeta> = {
  confirmed: { label: "Verified", color: "var(--co-live)" },
  approximate: { label: "Approximate date", color: "var(--co-attention)" },
  unconfirmed: { label: "Date unverified", color: "var(--co-danger)" },
};

export const CREATOR_TIER_LABELS: Record<string, string> = {
  nano: "Nano",
  micro: "Micro",
  mid: "Mid",
  macro: "Macro",
  celebrity: "Celebrity",
};

export const SOURCE_TYPE_LABELS: Record<string, string> = {
  report: "Report",
  platform_data: "Platform data",
  scrape: "Scrape",
  survey: "Survey",
  internal_benchmark: "Internal benchmark",
  news: "News",
  other: "Other",
};

/** Roadmap pillar emphasis -> how many of three dots are lit. */
export const EMPHASIS_META: Record<string, { label: string; dots: number }> = {
  heavy: { label: "Heavy", dots: 3 },
  standard: { label: "Standard", dots: 2 },
  lighter: { label: "Lighter", dots: 1 },
};

// ── Formatting helpers ───────────────────────────────────────────────────────

/** snake_case / camelCase -> "Title case". */
export function humanize(key: string): string {
  const spaced = key.replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Treats null/undefined/""/[]/{}  as empty. */
export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined || value === "") return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value as object).length === 0;
  return false;
}

/**
 * The period as the document header prints it: one year, or a span of two.
 * Derived from the row's own dates and nothing else — an undated strategy gets
 * no period rather than a guessed one.
 *
 * Lives here rather than in StrategyView because the public shared page prints
 * the same period from the same dates, and two copies would drift.
 */
export function footerPeriod(
  start: string | null | undefined,
  end: string | null | undefined,
): string | null {
  const from = start?.slice(0, 4) ?? "";
  const to = end?.slice(0, 4) ?? "";
  if (!/^\d{4}$/.test(from) || !/^\d{4}$/.test(to)) return null;
  return from === to ? from : `${from} – ${to}`;
}

/** "Jun 22, 2026" from a YYYY-MM-DD or ISO string; passes through on parse failure. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** Shift a YYYY-MM-DD date by `days` (local calendar). */
export function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Rounds a percentage for display; keeps one decimal only when it carries signal. */
export function pct(n: number): string {
  if (!Number.isFinite(n)) return "0";
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

export function near100(total: number): boolean {
  return Math.abs(total - 100) < 0.5;
}

export function sum(xs: number[]): number {
  return xs.reduce((a, b) => a + b, 0);
}
